/**
 * AdminControls - Drawer for coach/admin to manage CCM resources
 *
 * Allows coaches to:
 * - View all resources by section
 * - Add new resources (placeholder for now)
 * - See resource statistics
 */
import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Chip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupsIcon from '@mui/icons-material/Groups';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HubIcon from '@mui/icons-material/Hub';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import SchoolIcon from '@mui/icons-material/School';

// Section icons mapping
const SECTION_ICONS = {
  capacity: GroupsIcon,
  care: FavoriteIcon,
  capital: AccountBalanceIcon,
  communityModel: HubIcon,
};

// Entity type icons
const ENTITY_ICONS = {
  person: PersonIcon,
  organization: BusinessIcon,
  program: SchoolIcon,
  community: GroupsIcon,
};

// eslint-disable-next-line max-lines-per-function
function AdminControls({
  open,
  onClose,
  resources,
  sectionConfig,
  onAddResource,
}) {
  // Calculate statistics
  const totalResources = Object.values(resources).flat().length;
  const resourcesByType = Object.values(resources).flat().reduce((acc, r) => {
    acc[r.entityType] = (acc[r.entityType] || 0) + 1;
    return acc;
  }, {});

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 400, maxWidth: '100%' },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            CCM Resource Manager
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Coach administration tools
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Stats */}
      <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Resource Summary
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`${totalResources} Total`}
            size="small"
            sx={{ fontWeight: 600 }}
          />
          {Object.entries(resourcesByType).map(([type, count]) => {
            const Icon = ENTITY_ICONS[type] || PersonIcon;
            return (
              <Chip
                key={type}
                icon={<Icon sx={{ fontSize: 16 }} />}
                label={`${count} ${type}s`}
                size="small"
                variant="outlined"
              />
            );
          })}
        </Box>
      </Box>

      <Divider />

      {/* Add Resource Button */}
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={onAddResource}
          sx={{ mb: 2 }}
        >
          Add New Resource
        </Button>

        <Typography variant="caption" color="text.secondary">
          Note: In the full implementation, this will open a dialog to search and add entities from the system.
        </Typography>
      </Box>

      <Divider />

      {/* Resources by Section */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {Object.entries(sectionConfig).map(([sectionId, config]) => {
          const SectionIcon = SECTION_ICONS[sectionId] || GroupsIcon;
          const sectionResources = resources[sectionId] || [];

          return (
            <Accordion key={sectionId} defaultExpanded={sectionId === 'capacity'}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SectionIcon sx={{ color: config.color, fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 600 }}>
                    {config.title}
                  </Typography>
                  <Chip
                    label={sectionResources.length}
                    size="small"
                    sx={{
                      height: 20,
                      minWidth: 24,
                      bgcolor: `${config.color}15`,
                      color: config.color,
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List dense disablePadding>
                  {sectionResources.map((resource, index) => {
                    const EntityIcon = ENTITY_ICONS[resource.entityType] || PersonIcon;
                    return (
                      <ListItem
                        key={resource.id}
                        divider={index < sectionResources.length - 1}
                        sx={{ px: 2, py: 1 }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <EntityIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={resource.name}
                          secondary={resource.relevanceTag}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        <ListItemSecondaryAction>
                          <Chip
                            label={`${resource.matchScore}%`}
                            size="small"
                            sx={{
                              height: 20,
                              bgcolor: `${config.color}15`,
                              color: config.color,
                              fontSize: '0.7rem',
                            }}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                  {sectionResources.length === 0 && (
                    <ListItem sx={{ px: 2 }}>
                      <ListItemText
                        secondary="No resources in this section"
                        secondaryTypographyProps={{ sx: { fontStyle: 'italic' } }}
                      />
                    </ListItem>
                  )}
                </List>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Changes made in Coach Mode are saved automatically.
          Use the main tiles to reorder or remove resources.
        </Typography>
      </Box>
    </Drawer>
  );
}

export default AdminControls;
